<div class="page-head">
  <div>
    <h1 class="title">Providers</h1>
    <p class="desc">
      Airline NDC providers configured for your agency. Switch one off to hide its content from your
      agents.
    </p>
    @if (!canEdit) {
      <p class="desc">Only managers of your main agency can switch providers on or off.</p>
    }
  </div>
</div>

<div class="card mb-4">
  <div class="flex flex-wrap items-end gap-4 p-4">
    <div class="field">
      <label class="label" for="provider-q">Provider</label>
      <div class="input w-60">
        <app-icon name="search" class="text-muted-foreground" />
        <input id="provider-q" placeholder="Search by name or code…" [(ngModel)]="query" />
      </div>
    </div>
  </div>
</div>

<div class="tabs mb-4" role="tablist">
  @for (f of filters; track f.key) {
    <button
      type="button"
      role="tab"
      class="tab"
      [class.on]="statusFilter === f.key"
      [attr.aria-selected]="statusFilter === f.key"
      (click)="statusFilter = f.key"
    >
      {{ f.label }}
      <span class="badge badge-secondary">{{ count(f.key) }}</span>
    </button>
  }
</div>

<div class="card scroll-x">
  @if (!loaded) {
    <div class="flex flex-col gap-3 p-6">
      <div class="skeleton h-5 w-1/3"></div>
      <div class="skeleton h-5 w-2/3"></div>
      <div class="skeleton h-5 w-1/2"></div>
    </div>
  } @else if (!rows.length) {
    <div class="empty">
      {{ allRows.length ? 'No providers match your filters.' : 'No providers configured yet.' }}
    </div>
  } @else {
    <table class="tbl">
      <thead>
        <tr>
          <th>
            <button type="button" class="sort" (click)="toggleSort()">
              Provider
              <app-icon name="sort" class="sm" />
            </button>
          </th>
          <th>Agency ID</th>
          <th>PCC</th>
          <th>Status</th>
          <th class="num">Enabled</th>
        </tr>
      </thead>
      <tbody>
        @for (row of rows; track row.provider.id) {
          <tr>
            <td>
              <div class="flex items-center gap-3">
                <span class="airline" [class.off]="!isLive(row)">{{ row.provider.agw_id }}</span>
                <div class="flex flex-col">
                  <span class="font-medium">{{ row.provider.name || row.provider.agw_id }}</span>
                  <span class="text-xs text-muted-foreground">NDC API</span>
                </div>
              </div>
            </td>
            <td>
              @if (row.identifiers.agency_id) {
                <span class="code">{{ row.identifiers.agency_id }}</span>
              } @else {
                <span class="text-muted-foreground">—</span>
              }
            </td>
            <td>
              @if (row.identifiers.pcc) {
                <span class="code">{{ row.identifiers.pcc }}</span>
              } @else {
                <span class="text-muted-foreground">—</span>
              }
            </td>
            <td>
              @if (!row.active) {
                <span
                  class="status text-muted-foreground"
                  title="Switched off by AirGateway. Contact support to have it switched back on."
                >
                  <span class="dot"></span>
                  Unavailable
                </span>
              } @else {
                <span class="status" [class.text-muted-foreground]="!row.enabled">
                  <span class="dot" [class.dot-on]="row.enabled"></span>
                  {{ row.enabled ? 'Active' : 'Inactive' }}
                </span>
              }
            </td>
            <td class="num">
              <button
                type="button"
                role="switch"
                class="switch"
                [class.on]="row.enabled"
                [attr.aria-checked]="row.enabled"
                [attr.aria-label]="(row.provider.name || row.provider.agw_id) + ' enabled'"
                [disabled]="saving || !canEdit"
                (click)="onToggle(row)"
              ></button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  }
</div>
@if (loaded && allRows.length) {
  <div class="flex items-center justify-between px-1 py-3 text-xs text-muted-foreground">
    <span>{{ allRows.length }} providers</span>
    @if (canEdit) {
      <span>Changes are saved immediately and apply to all your agencies.</span>
    }
  </div>
}

<app-confirm-dialog
  [open]="!!pendingDeactivation"
  [title]="
    'Deactivate ' +
    (pendingDeactivation?.provider?.name || pendingDeactivation?.provider?.agw_id) +
    '?'
  "
  confirmLabel="Deactivate"
  [destructive]="true"
  (confirm)="confirmDeactivation()"
  (cancel)="pendingDeactivation = null"
>
  Agents in all your agencies will stop seeing
  {{ pendingDeactivation?.provider?.name || pendingDeactivation?.provider?.agw_id }} offers in
  BookingPad and partner apps. You can switch it back on at any time.
</app-confirm-dialog>
